import { useCallback, useEffect, useState } from "react";
import { getIncomeExpenseReport } from "../../services/incomeExpenseReport";
import {
  IncomeExpenseReport,
  IncomeExpenseReportParams,
} from "../../types/income-expense-report.type";

interface Answer {
  params: IncomeExpenseReportParams;
  attempt: number;
  /** null when the request failed. */
  report: IncomeExpenseReport | null;
}

/**
 * The report for `params` (stable identity), asked again when they change or on `reload`, and
 * not asked while they are null. A newer filter ABORTS the request still in flight, so the
 * server stops working for an answer nobody will read, and nothing is returned until the current
 * params are answered — a new filter never shows the previous one's figures under it.
 */
export function useIncomeExpenseReport(params: IncomeExpenseReportParams | null) {
  const [answer, setAnswer] = useState<Answer>();
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (params === null) return undefined;
    const controller = new AbortController();
    getIncomeExpenseReport(params, controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) {
          setAnswer({ params, attempt, report: response.data.data });
        }
      })
      .catch(() => {
        // An aborted request was replaced on purpose; any other failure has already been shown
        // by the interceptor, and the screen offers ลองใหม่ instead of the previous figures.
        if (!controller.signal.aborted) setAnswer({ params, attempt, report: null });
      });
    return () => controller.abort();
  }, [params, attempt]);

  const reload = useCallback(() => setAttempt((previous) => previous + 1), []);

  const current =
    params !== null && answer?.params === params && answer.attempt === attempt
      ? answer
      : undefined;

  return {
    report: current?.report ?? null,
    isLoading: params !== null && current === undefined,
    isFailed: current !== undefined && current.report === null,
    reload,
  };
}
